import {AfterViewInit, Component, ViewChild} from '@angular/core';
import {PeoService} from '../../../../service/peo.service';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatFooterCell, MatFooterCellDef,
  MatFooterRow,
  MatFooterRowDef,
  MatHeaderCell, MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef, MatNoDataRow, MatRow, MatRowDef, MatTable,
  MatTableDataSource
} from '@angular/material/table';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {FormsModule} from '@angular/forms';
import {PeoApiCredentialModel} from '../../../../model/peo-api-credential.model';
import {NgIf} from '@angular/common';
import {
  MatCard,
  MatCardAvatar,
  MatCardContent,
  MatCardHeader,
  MatCardSubtitle,
  MatCardTitle
} from '@angular/material/card';
import {MatFormField, MatLabel, MatSuffix} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatInput} from '@angular/material/input';
import {MatTooltip} from '@angular/material/tooltip';
import {MatIconButton} from '@angular/material/button';
import {PeoApiCredentialTestModel} from '../../../../model/peo-api-credential-test.model';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatSnackBar} from '@angular/material/snack-bar';
import {UtilityService} from '../../../../service/utility.service';

/**
 * @class
 * @component
 * @description This class maintains the sign-out page component of our application.
 * @export
 * @name PeoApiCredentialTestAdminPageComponent
 */
@Component({
  imports: [
    FormsModule,
    MatCard,
    MatCardAvatar,
    MatCardContent,
    MatCardHeader,
    MatCardSubtitle,
    MatCardTitle,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatFooterCell,
    MatFooterRow,
    MatFooterRowDef,
    MatFormField,
    MatHeaderCell,
    MatHeaderRow,
    MatHeaderRowDef,
    MatIcon,
    MatInput,
    MatLabel,
    MatPaginator,
    MatRow,
    MatRowDef,
    MatSort,
    MatSortHeader,
    MatSuffix,
    MatTable,
    MatTooltip,
    NgIf,
    MatFooterCellDef,
    MatHeaderCellDef,
    MatIconButton,
    MatNoDataRow,
    MatProgressSpinner
  ],
  selector: 'app-page-admin-peo-api-credential-test',
  standalone: true,
  styleUrl: './peo-api-credential-test.component.scss',
  templateUrl: './peo-api-credential-test.component.html'
})
export class PeoApiCredentialTestAdminPageComponent implements AfterViewInit {

  /**
   * @description This property contains the batch size for testing the PEO API Credentials.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @name PeoApiCredentialTestAdminPageComponent#BatchSize
   * @property
   * @private
   * @static
   * @type {number}
   */
  private static BatchSize: number = 3;

  /**
   * @description This property contains the PEOs registered in the system.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @name PeoApiCredentialTestAdminPageComponent#peoDataSource
   * @property
   * @protected
   * @type {MatTableDataSource<PeoApiCredentialModel>}
   */
  protected credentialDataSource: MatTableDataSource<PeoApiCredentialModel> =
    new MatTableDataSource<PeoApiCredentialModel>();

  /**
   * @description This property contains the PEO API Credential toggles.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @name PeoApiCredentialTestAdminPageComponent#credentialToggles
   * @property
   * @protected
   * @type {{[key: string]: { error?: Error, hidePassword: boolean, tested: boolean, testedSuccessfully: boolean, testing: boolean }}}
   */
  protected credentialToggles: {
    [key: string]: { error?: Error, hidePassword: boolean, tested: boolean, testedSuccessfully: boolean, testing: boolean }
  } = {};

  /**
   * @description This property contains the columns for the PEO API Credentials table that can be filtered.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @name PeoApiCredentialTestAdminPageComponent#filterColumns
   * @property
   * @protected
   * @readonly
   * @type {(keyof PeoApiCredentialModel)[]}
   */
  protected readonly filterColumns: (keyof PeoApiCredentialModel)[] = ['password', 'peoId', 'username', 'url'];

  /**
   * @description This property contains the filter text for the PEOs table.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @name PeoApiCredentialTestAdminPageComponent#filterInput
   * @property
   * @protected
   * @type {string}
   */
  protected filterInput: string = UtilityService.EmptyString;

  /**
   * @description This property contains the flag to hide or show all passwords.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @name PeoApiCredentialTestAdminPageComponent#hidePasswords
   * @property
   * @protected
   * @type {boolean}
   */
  protected hidePasswords: boolean = true;

  /**
   * @description This property contains the loading status of the PEO API Credentials table.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @name PeoApiCredentialTestAdminPageComponent#loading
   * @property
   * @protected
   * @type {boolean}
   */
  protected loading: boolean = false;

  /**
   * @description This property contains the paginator for the PEOs table.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @name PeoApiCredentialTestAdminPageComponent#paginator
   * @property
   * @protected
   * @type {MatPaginator}
   */
  @ViewChild(MatPaginator)
  protected paginator!: MatPaginator;

  /**
   * @description This property contains the sorter for the PEOs table.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @name PeoApiCredentialTestAdminPageComponent#sort
   * @property
   * @protected
   * @type {MatSort}
   */
  @ViewChild(MatSort)
  protected sort!: MatSort;

  /**
   * @description This property contains the columns for the PEOs table.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @name PeoApiCredentialTestAdminPageComponent#tableColumns
   * @property
   * @protected
   * @type {string[]}
   */
  protected tableColumns: string[] = ['id', 'username', 'password', 'url', '_actions'];

  /**
   * @description This property denotes whether the API credentials have been tested or not.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @name PeoApiCredentialTestAdminPageComponent#tested
   * @property
   * @protected
   * @protected
   */
  protected tested: boolean = false;

  /**
   * @description This property denotes whether the API credentials have been tested successfully or not.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @name PeoApiCredentialTestAdminPageComponent#testedSuccessfully
   * @property
   * @protected
   * @type {boolean}
   */
  protected testedSuccessfully: boolean = false;

  /**
   * @description This property denotes whether the API credentials are currently being tested or not.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @name PeoApiCredentialTestAdminPageComponent#testing
   * @property
   * @protected
   * @type {boolean}
   */
  protected testing: boolean = false;

  /**
   * @constructor
   * @description This method instantiates a new instance of our peo page component with injected dependencies.
   * @param {PeoService} service The injected instance of our PrismHR PEO service provider.
   * @param {MatSnackBar} snackbar The injected instance of our Angular Material snack bar service provider.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @name PeoApiCredentialTestAdminPageComponent#constructor()
   * @public
   */
  public constructor(
    /**
     * @description This property contains the injected instance of our PrismHR Employee service provider.
     * @memberof PeoApiCredentialTestAdminPageComponent
     * @name PeoApiCredentialTestAdminPageComponent#service
     * @private
     * @readonly
     * @type {PeoService}
     */
    private readonly service: PeoService,
    /**
     * @description This property contains the injected instance of our Angular Material snack bar service provider.
     * @memberof PeoApiCredentialTestAdminPageComponent
     * @name PeoApiCredentialTestAdminPageComponent#snackbar
     * @private
     * @property
     * @readonly
     * @type {MatSnackBar}
     */
    private readonly snackbar: MatSnackBar
  ) {

    // Add our custom table filter predicate.
    this.credentialDataSource.filterPredicate =
      UtilityService.TableMultiFilterPredicate<PeoApiCredentialModel>(this.filterColumns);
  }

  /**
   * @description This method checks the credential toggles for the PEO API Credentials.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @method
   * @name PeoApiCredentialTestAdminPageComponent#checkCredentialToggles()
   * @private
   * @returns {void}
   */
  private checkCredentialToggles(): void {

    // Reset the tested flag in the instance.
    this.tested = Object.keys(this.credentialToggles).every((t: string): boolean => this.credentialToggles[t].tested);

    // Reset the tested successfully flag in the instance.
    this.testedSuccessfully = Object.keys(this.credentialToggles).every((t: string): boolean =>
      this.credentialToggles[t].testedSuccessfully);

    // Localize the batch-testing flag.
    const batchTesting: boolean = Object.keys(this.credentialToggles).some((t: string): boolean =>
      this.credentialToggles[t].testing);

    // Reset the testing flag in the instance.
    this.testing = !!this.credentialDataSource.data.filter((c: PeoApiCredentialModel): boolean =>
      !this.credentialToggles[c.peoId].tested).length;

    // Check to see if we're still testing and the number of tests still in progress, then execute the next batch of tests.
    if (!this.tested && this.testing && !batchTesting) this.testCredential(undefined, true);
  }

  /**
   * @description This method loads the PEO API Credential details.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @method
   * @name PeoApiCredentialTestAdminPageComponent#loadCredentialList()
   * @private
   * @returns {void}
   */
  private loadCredentialList(): void {

    // Reset the loading flag.
    this.loading = true;

    // Reset the data on the PEO API Credentials table.
    this.credentialDataSource.data = [];

    // Load the PEO API Credential details from the .NET backend API.
    this.service.listApiCredentials().subscribe({

      // Handle the completion event.
      complete: (): boolean => this.loading = false,

      // Handle any errors that occur.
      error: (error: Error): void => {

        // Display a snack bar message to the user.
        this.snackbar.open(`Loading PEO API Credentials failed with:  ${error.message}`, 'Dismiss', {
          duration: 5000,
          horizontalPosition: 'end',
          panelClass: ['snackbar-error'],
          verticalPosition: 'bottom'
        });
      },

      // Handle the next event.
      next: (c: PeoApiCredentialModel[]): void => {

        // Reset the list of PEO API Credentials.
        this.credentialDataSource.data = c;

        // Iterate over the PEO API Credentials and set the toggle flags.
        for (const i of c) {

          // Set the PEO API Credential toggle flags.
          this.credentialToggles[i.peoId] = {

            // We'll want to hide the password by default.
            hidePassword: true,

            // We haven't tested the PEO API Credential yet.
            tested: false,

            // We haven't tested the PEO API Credential successfully yet.
            testedSuccessfully: false,

            // We're not currently testing the PEO API Credential.
            testing: false
          };
        }

        // Set the paginator for the PEO API Credentials table.
        this.credentialDataSource.paginator = this.paginator;

        // Set the sorter for the PEO API Credentials table.
        this.credentialDataSource.sort = this.sort;
      }
    });
  }

  /**
   * @description This method updates the credential toggles for the provided PEO ID.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @method
   * @name PeoApiCredentialTestAdminPageComponent#updateCredentialToggles()
   * @param {string} peoId The ID of the PEO API Credential to update the toggles for.
   * @param {boolean} skipGlobalUpdate The flag to skip the global update of the credential toggles.
   * @param {Error?} [error] The error that occurred while testing the PEO API Credential.
   * @param {boolean?} [tested] The tested flag for the PEO API Credential.
   * @param {boolean?} [testedSuccessfully] The tested successfully flag for the PEO API Credential.
   * @param {boolean?} [testing] The testing flag for the PEO API Credential.
   * @private
   * @returns {void}
   */
  protected updateCredentialToggles(peoId: string, skipGlobalUpdate: boolean, error?: Error, tested?: boolean,
                                    testedSuccessfully?: boolean, testing?: boolean): void {

    // Update the credential toggles for the provided PEO ID.
    this.credentialToggles[peoId] = {

      // Reset the error if provided.
      error: error ?? this.credentialToggles[peoId].error,

      // Pass the hide-password flag through.
      hidePassword: this.credentialToggles[peoId].hidePassword,

      // Reset the tested flag if provided.
      tested: tested ?? this.credentialToggles[peoId].tested,

      // Reset the tested successfully flag if provided.
      testedSuccessfully: testedSuccessfully ?? this.credentialToggles[peoId].testedSuccessfully,

      // Reset the testing flag if provided.
      testing: testing ?? this.credentialToggles[peoId].testing
    };

    // Check the skip global update flag.
    if (!skipGlobalUpdate) this.checkCredentialToggles();
  }

  /**
   * @description This method clears the filter on the PEO API Credentials table.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @method
   * @name PeoApiCredentialTestAdminPageComponent#clearFilter()
   * @protected
   * @returns {void}
   */
  protected clearFilter(): void {

    // Clear the filter input.
    this.filterInput = UtilityService.EmptyString;

    // Clear the filter on the PEOs in the instance.
    this.credentialDataSource.filter = UtilityService.EmptyString;

    // Check for a paginator, then navigate to the first page.
    if (this.credentialDataSource.paginator) this.credentialDataSource.paginator.firstPage();
  }

  /**
   * @description This method downloads the report for the PEO API Credential tests.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @method
   * @name PeoApiCredentialTestAdminPageComponent#downloadReport()
   * @protected
   * @returns {void}
   */
  protected downloadReport(): void {

    // Define our data container.
    const csvData: string[][] = [
      ['PeoId', 'Username', 'Password', 'Url', 'Status']
    ];

    // Iterate over the PEO API Credentials.
    for (const c of this.credentialDataSource.data) {

      // Define our row container.
      const row: string[] = [c.peoId, c.username, !this.credentialToggles[c.peoId].hidePassword ||
      !this.credentialToggles[c.peoId].testedSuccessfully ? c.password : '*'.repeat(c.password.length),
        c.url, this.credentialToggles[c.peoId].testedSuccessfully ? 'Pass' : 'Fail'];

      // Quote any values that contain commas.
      csvData.push(row.map((v: string): string => v.includes(',') ? `"${v.replace(/"/g, '""')}"` : v));
    }

    // Define our CSV content.
    const csvContent: string = csvData.map((r: string[]): string => r.join(',')).join('\n');

    // Define our blob.
    const blob: Blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});

    // Define our URL.
    const url: string = window.URL.createObjectURL(blob);

    // Define our anchor.
    const anchor: HTMLAnchorElement = document.createElement('a');

    // Set the anchor's attributes.
    anchor.href = url;

    // Set the anchor's download attribute.
    anchor.download = 'peo-api-credential-test-report.csv';

    // Append the anchor to the document body.
    document.body.appendChild(anchor);

    // Click the anchor.
    anchor.click();

    // Remove the anchor from the document body.
    document.body.removeChild(anchor);

    // Revoke the URL.
    window.URL.revokeObjectURL(url);
  }

  /**
   * @description This method filters the PEO API Credentials in the table.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @method
   * @name PeoApiCredentialTestAdminPageComponent#filterTable()
   * @param {Event} event The event that triggered the filter.
   * @protected
   * @returns {void}
   */
  protected filterTable(event: Event): void {

    // Reset the filter input.
    this.filterInput = (event.target as HTMLInputElement).value ?? UtilityService.EmptyString;

    // Ensure we have a filter input.
    if (!this.filterInput) return this.clearFilter();

    // Localize the columnar filter value.
    const filterValue: { [key: string]: string } = {};

    // Iterate over the filter columns and add the filter value to the filter input.
    this.filterColumns.forEach((c: string) => filterValue[c] = this.filterInput.trim().toLowerCase());

    // Apply the filter to the data table in the instance.
    this.credentialDataSource.filter = JSON.stringify(filterValue);

    // Check for a paginator, then navigate to the first page.
    if (this.credentialDataSource.paginator) this.credentialDataSource.paginator.firstPage();
  }

  /**
   * @description This method tests the PEO API Credential for the provided PEO ID.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @method
   * @name PeoApiCredentialTestAdminPageComponent#testCredential()
   * @param {string} [peoId] The ID of the PEO API Credential to test.
   * @param {boolean} [batch] The flag to test all PEO API Credentials.
   * @protected
   */
  protected testCredential(peoId: string | undefined = undefined, batch: boolean = false): void {

    // Check if a PEO ID was provided, then test the PEO API Credential for that PEO.
    if (!!peoId) {

      // Set the testing flag for the provided PEO API Credential.
      this.updateCredentialToggles(peoId, true, undefined, undefined, undefined, true);

      // Test the PEO API Credential for the provided PEO.
      this.service.testApiCredentialsForPeo(peoId).subscribe({

        // Handle any errors that occur.
        error: (e: Error): void => {

          // Update the toggled flags on the PEO.
          this.updateCredentialToggles(peoId, true, e, true, false, false);

          // Display a snack bar message to the user.
          this.snackbar.open(`PrismHR API Credential Test for ${peoId} failed with:  ${e.message}`, 'Dismiss', {
            duration: 5000,
            horizontalPosition: 'end',
            panelClass: ['snackbar-error'],
            verticalPosition: 'bottom'
          });
        },

        // Handle the next event.
        next: (result: PeoApiCredentialTestModel): void => {

          // Update the toggled flags on the PEO.
          this.updateCredentialToggles(peoId, true, undefined, true, result.session, false);

          // Check if the PEO API Credential was tested successfully.
          if (!this.credentialToggles[peoId].testedSuccessfully)
            this.snackbar.open(`PrismHR API Credential Test for ${peoId} Failed.`, 'Dismiss', {
              duration: 5000,
              horizontalPosition: 'end',
              panelClass: ['snackbar-error'],
              verticalPosition: 'bottom'
            });
        }
      });

      // We're done, so return.
      return;
    }

    // Check for a batch.
    if (!batch) {

      // Reset the tested flag in the instance.
      this.tested = false;

      // Reset the tested successfully flag in the instance.
      this.testedSuccessfully = false;

      // Reset the testing flag in the instance.
      this.testing = true;

      // Iterate over the PEO API Credentials and set the toggle flags.
      for (const t in this.credentialToggles)
        this.updateCredentialToggles(t, false, undefined, false, undefined, false);
    }

    // Localize our batch of tests to run then run them.
    this.credentialDataSource.data.filter((c: PeoApiCredentialModel): boolean => !!this.credentialToggles[c.peoId] &&
      !this.credentialToggles[c.peoId].tested).slice(0, PeoApiCredentialTestAdminPageComponent.BatchSize)
      .forEach((c: PeoApiCredentialModel): void => {

        // Set the testing flag for the provided PEO API Credential.
        this.updateCredentialToggles(c.peoId, true, undefined, undefined, undefined, true);

        // Test the PEO API Credential for the provided PEO.
        this.service.testApiCredentials(c).subscribe({
          // Handle any errors that occur.
          error: (e: Error): void => {

            // Update the toggled flags on the PEO.
            this.updateCredentialToggles(c.peoId, false, e, true, false, false);

            // Display a snack bar message to the user.
            this.snackbar.open(`Testing PrismHR API Credentials for ${c.peoId} failed with:  ${e.message}`, 'Dismiss', {
              duration: 5000,
              horizontalPosition: 'end',
              panelClass: ['snackbar-error'],
              verticalPosition: 'bottom'
            });
          },

          // Handle the next event.
          next: (result: PeoApiCredentialTestModel): void => {

            // Update the toggled flags on the PEO.
            this.updateCredentialToggles(c.peoId, false, undefined, true, result.session, false);

            // Check if the PEO API Credential was tested successfully.
            if (!this.credentialToggles[c.peoId].testedSuccessfully)
              this.snackbar.open(`Testing PrismHR API Credentials for ${c.peoId} Failed.`, 'Dismiss', {
                duration: 5000,
                horizontalPosition: 'end',
                panelClass: ['snackbar-error'],
                verticalPosition: 'bottom'
              });
          }
        })
      });
  }

  /**
   * @description This method toggles the visibility if the password field.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @method
   * @name PeoApiCredentialTestAdminPageComponent#togglePasswordVisibility()
   * @param {string} [peoId] The ID of the PEO API Credential to toggle the password visibility for.
   * @protected
   * @returns {void}
   */
  protected togglePasswordVisibility(peoId?: string): void {

    // Check if a PEO ID was provided, then toggle the password visibility for that PEO API Credential set.
    if (peoId) {

      // Toggle the password visibility for the provided PEO API Credential.
      this.credentialToggles[peoId].hidePassword = !this.credentialToggles[peoId].hidePassword;

      // We're done, so return.
      return;
    }

    // Otherwise, toggle the password visibility for all PEO API Credentials.
    for (const t in this.credentialToggles)
      this.credentialToggles[t].hidePassword = !this.credentialToggles[t].hidePassword;

    // Reset the hide passwords flag.
    this.hidePasswords = !this.hidePasswords;
  }

  /**
   * @description This method initializes the component.
   * @memberof PeoApiCredentialTestAdminPageComponent
   * @method
   * @name PeoApiCredentialTestAdminPageComponent#ngAfterViewInit()
   * @public
   * @returns {void}
   */
  public ngAfterViewInit(): void {

    // Load the PEO API Credential details.
    this.loadCredentialList();
  }
}
